import { createResetExecutionApi } from './reset-execution-api'
import { withPresentationDeadline, assertPresentationActive } from './i18n/chatPresentationDeadline'
import { createScheduleActionSender } from './schedule-action-permissions'
import { invitationForbiddenBody } from './invitation-forbidden'
import { useAuthStore } from '../shared/stores/auth'
import { getEngineUrl } from './config'
import { handleUnauthorized, markSessionHealthy, rearmLogout } from './auth-recovery'
import {
  authorizePresentationSend,
  capturePresentationOwner,
  presentationHeaders,
} from './i18n/chatPresentationOwner'
import type {
  EngineMultiSheetProposal,
  EngineMultiSheetResult,
  ExecuteImportBody,
  ExecuteImportEnqueuedResult,
} from '../types/import'
import type {
  ImportFailedRowsResponse,
  ImportJobStatusResponse,
  ImportReimportResponse,
  ImportRowCorrection,
  ImportSkippedRowsResponse,
} from '../types/rich-content'
import type { LtpScheduleEnvelope, ScheduleCriticality } from '../types/ltp-schedule'
import {
  RESET_EXECUTION_PATH,
  RESET_PASSWORD_PATH,
  RESET_REAUTH_ATTEMPT_PATH,
  resetPasswordInputSchema,
  resetReauthAttemptSchema,
} from './reset-contract'

const BASE_URL = getEngineUrl()
export const EQUIPMENT_REGISTRATION_COMMIT_PATH = '/api/v1/equipment-registration/commit'
export const EQUIPMENT_REGISTRATION_SYNC_PATH = '/api/v1/equipment-registration/sync'

/**
 * Call this when the user successfully re-authenticates (e.g. in AuthGuard on
 * fresh Clerk session) so subsequent requests are allowed through.
 */
export function resetAuthErrorFlag(): void {
  rearmLogout()
}

class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    /**
     * RAIZ 9 (2026-07-25) — corpo JSON da resposta de erro, quando o servidor
     * mandou um. Antes o corpo cru virava SÓ a `message`, então quem tratava o
     * erro tinha que adivinhar por substring e o motivo real acabava escondido
     * do usuário. Com o corpo estruturado aqui, o chamador lê `code` e os dados
     * da recusa (por exemplo as abas que aguardam confirmação) e decide o que
     * mostrar. `undefined` quando a resposta não era JSON.
     */
    public body?: Record<string, unknown>,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

/**
 * Corpo estruturado de uma recusa do motor, quando existe. PURA.
 *
 * Detecção ESTRUTURAL (um erro que carrega `body` objeto), não `instanceof`: a
 * classe não é exportada, e amarrar a leitura à identidade dela tornaria o
 * consumidor intestável e quebraria se o módulo fosse carregado duas vezes no
 * bundle. O que importa aqui é o dado, não a linhagem do objeto.
 */
export function apiErrorBody(err: unknown): Record<string, unknown> | undefined {
  if (err === null || typeof err !== 'object') return undefined
  const body = (err as { body?: unknown }).body
  if (body === null || typeof body !== 'object' || Array.isArray(body)) return undefined
  return body as Record<string, unknown>
}

/**
 * Status HTTP de uma recusa, quando o erro carrega um. PURA.
 *
 * Existe porque `request()` corta 401, 403 e 429 ANTES de ler o corpo — nesses
 * três o `code` do motor nunca chega, e o status é a ÚNICA coisa que sobrou
 * para distinguir "sua sessão caiu" de "seu perfil não permite" de "a rede
 * caiu". Sem ele os três liam como a mesma falha opaca.
 *
 * Leitura ESTRUTURAL, não `instanceof`, pelo mesmo motivo de `apiErrorBody`:
 * `ApiError` não é exportada, e amarrar-se à identidade da classe quebraria se
 * o módulo fosse carregado duas vezes no bundle.
 */
export function apiErrorStatus(err: unknown): number | undefined {
  if (err === null || typeof err !== 'object') return undefined
  const status = (err as { status?: unknown }).status
  return typeof status === 'number' ? status : undefined
}

/**
 * RAIZ 9 — constrói o erro de uma resposta não-2xx PRESERVANDO o corpo. A
 * mensagem passa a ser o campo `error` do corpo quando existe, em vez do JSON
 * cru; o corpo inteiro fica disponível em `err.body` para quem precisa do
 * `code`. Um corpo não-JSON continua virando a mensagem crua, como antes.
 *
 * Usada nos três pontos que já lançavam `ApiError(res.status, text)`: sem isso,
 * qual dos três atendeu a chamada decidiria se o motivo chega ou não.
 */
function toApiError(status: number, text: string): ApiError {
  let body: Record<string, unknown> | undefined
  try {
    const parsed: unknown = JSON.parse(text)
    if (parsed !== null && typeof parsed === 'object' && !Array.isArray(parsed)) {
      body = parsed as Record<string, unknown>
    }
  } catch {
    body = undefined
  }
  const message = typeof body?.['error'] === 'string' ? body['error'] : text
  return new ApiError(status, message, body)
}

/**
 * Build request headers with tenant/user IDs and Clerk JWT.
 *
 * Uses the Clerk `getToken()` function stored in authStore
 * to obtain a fresh Bearer token for every request.
 *
 * S3 US-3.7 — fail loud when tenantId is missing instead of sending
 * an empty `X-Tenant-Id` header (which the engine would reject with 4xx
 * after the network round-trip). Forces the caller to authenticate via
 * Clerk before any REST call leaves the SPA.
 */
async function getHeaders(withBody = false): Promise<Record<string, string>> {
  const { tenantId, userId, getToken } = useAuthStore.getState()
  if (!tenantId) {
    throw new ApiError(401, 'Missing tenantId — user must authenticate via Clerk')
  }
  const headers: Record<string, string> = {
    'X-Tenant-Id': tenantId,
    'X-User-Id': userId,
  }
  if (withBody) {
    headers['Content-Type'] = 'application/json'
  }
  const token = await getToken()
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }
  return headers
}

const DEFAULT_TIMEOUT_MS = 180_000

/** Turns a non-2xx engine response into the right `ApiError`; resolves only for 2xx. */
async function throwIfRefused(res: Response, path: string): Promise<void> {
  if (res.status === 401) {
    // R63 — ponto único (auth-recovery.ts): renova o passe, avisa, e só desloga se não der.
    await handleUnauthorized()
    throw new ApiError(401, 'Session expired')
  }

  // Reset da base (#141): 403/429 carregam o MOTIVO (senha incorreta, conta sem senha, bloqueio
  // com tempo restante) — o corpo é preservado para a tela explicar, em vez de "Permission denied".
  if ((res.status === 403 || res.status === 429) && path.startsWith(RESET_EXECUTION_PATH)) {
    const error = toApiError(res.status, await res.text().catch(() => ''))
    throw new ApiError(
      res.status,
      res.status === 403 ? 'Permission denied' : 'Too many requests',
      error.body,
    )
  }

  // Commit e sync do cadastro (spec 2026-10-24 §3.5): a recusa 403 traz um RECIBO
  // com o código (permissão × dono divergente) e o snapshot — o corpo fica.
  if (
    res.status === 403 &&
    (path === EQUIPMENT_REGISTRATION_COMMIT_PATH || path === EQUIPMENT_REGISTRATION_SYNC_PATH)
  ) {
    throw toApiError(403, await res.text().catch(() => 'Permission denied'))
  }

  // 403 — caller has no permission; never retry
  if (res.status === 403) {
    if (!/^\/api\/v1\/invitations\/[^/]+\/accept$/.test(path))
      throw new ApiError(403, 'Permission denied')
    const error = toApiError(403, await res.text().catch(() => 'Permission denied'))
    throw new ApiError(403, 'Permission denied', invitationForbiddenBody(error.body))
  }

  // 429 — pass status through so callers can surface Retry-After messaging
  if (res.status === 429) {
    throw new ApiError(429, 'Too many requests')
  }

  if (!res.ok) {
    const text = await res.text().catch(() => 'Unknown error')
    throw toApiError(res.status, text)
  }
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  timeoutMs: number = DEFAULT_TIMEOUT_MS,
  extraHeaders?: Record<string, string>,
  presentationOwner?: { tenantId: string; userId: string },
  beforeSend?: () => void,
): Promise<T> {
  const ensureOwner = capturePresentationOwner(presentationOwner)
  const hasBody = body !== undefined && body !== null
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), timeoutMs)
  const readHeaders = () => getHeaders(hasBody)
  const headers = {
    ...(await (
      presentationOwner ? presentationHeaders(readHeaders, presentationOwner) : readHeaders()
    ).catch((error: unknown) => {
      clearTimeout(timer)
      throw error
    })),
    ...(extraHeaders ?? {}),
  }
  authorizePresentationSend(ensureOwner, beforeSend, () => clearTimeout(timer))
  let res: Response
  try {
    res = await fetch(`${BASE_URL}${path}`, {
      method,
      headers,
      body: hasBody ? JSON.stringify(body) : undefined,
      signal: controller.signal,
    })
  } catch {
    if (controller.signal.aborted) {
      throw new ApiError(0, 'Request timed out. The server took too long to respond.')
    }
    throw new ApiError(0, 'Connection lost. Check your network.')
  } finally {
    clearTimeout(timer)
  }

  ensureOwner()
  await throwIfRefused(res, path)

  // Successful response — reset the auth-error guard so re-authenticated
  // sessions can proceed normally.
  markSessionHealthy()

  return res.json() as Promise<T>
}

/** Only presentation traffic uses this whole-operation deadline; business writes stay unchanged. */
function requestPresentation<T>(body: unknown, owner: { tenantId: string; userId: string }) {
  const ensureOwner = capturePresentationOwner(owner)
  return withPresentationDeadline(async (signal) => {
    // R63/R74 — 401 durante a renovação do login: renova o passe (auth-recovery.ts) e
    // refaz UMA vez, na mesma janela de 25 s. Sem logout e sem virar falha de tradução.
    for (let attempt = 0; ; attempt++) {
      const headers = await presentationHeaders(() => getHeaders(true), owner, signal)
      assertPresentationActive(signal)
      ensureOwner()
      const res = await fetch(`${BASE_URL}/api/v1/i18n/presentation/translate`, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
        signal,
      })
      assertPresentationActive(signal)
      ensureOwner()
      if (res.status === 401 && attempt === 0 && (await handleUnauthorized()) === 'renewed') {
        assertPresentationActive(signal)
        ensureOwner()
        continue
      }
      await throwIfRefused(res, '/api/v1/i18n/presentation/translate')
      const result = (await res.json()) as T
      assertPresentationActive(signal)
      ensureOwner()
      markSessionHealthy()
      return result
    }
  }, 25_000)
}

/**
 * Admin dashboard snapshot — engine `GET /api/v1/admin/dashboard`.
 * Server-gated by `requireAdmin` (admin-auth-fix 2026-06-16): a non-admin token
 * gets 403, which the shared `request` wrapper surfaces as `ApiError(403)`.
 */
export interface AdminDashboardResponse {
  data: {
    totalEquipments: number
    activeEquipments: number
    openWorkOrders: number
    openNotifications: number
    criticalNotifications: number
    activeMaintPlans: number
    /**
     * E2 — planos em RASCUNHO (`active:false`). O onboarding grava assim por
     * invariante do motor, e o painel só contava ativos: quem terminava o
     * onboarding via "0" logo depois de o Axis dizer que o plano foi
     * registrado. Opcional para o painel não quebrar contra um motor antigo.
     */
    draftMaintPlans?: number
    completedWoThisMonth: number
    pmc: number
    backlog: number
    totalPlants: number
    totalLocations: number
  }
}

/**
 * Specialty (CRUD de Especialidades) — uma disciplina técnica do tenant.
 * `slug` é estável/imutável após criação; `name` é editável; seeds do sistema
 * (`isDefault=true`) não podem ser hard-deletados nem desativados via API.
 * Endpoint `GET/POST/PATCH/DELETE /api/v1/specialties` é ADMIN-ONLY (403 p/ não-admin).
 */
export interface AdminSpecialty {
  id: string
  tenantId: string
  slug: string
  /** Nome cru gravado (pt). É o que a edição/gravação do admin envia de volta. */
  name: string
  /**
   * Nome traduzido para o idioma do usuário, enviado pelo motor (#3379). Só para
   * EXIBIÇÃO — nunca gravar. Ausente em motor antigo: quem exibe cai no `name`.
   */
  displayName?: string
  active: boolean
  isDefault: boolean
  sortOrder: number
  createdAt: string
  updatedAt: string
  deletedAt: string | null
}

/**
 * RESET TOTAL DA BASE — availability probe + result (admin-reset-total).
 * The engine `GET /admin/reset-demo` reports whether the in-admin base-reset
 * control should even be SHOWN on this base (false on prod → invisible). The
 * `POST` performs the destructive wipe + reseed, gated by Owner/Admin membership
 * plus the tenant's own RESET password, defined by Owner/Admin in the Danger Zone (20/10/2026).
 */
export interface AdminResetAvailability {
  available: boolean
  environment: string
  /** Se o inquilino já definiu a senha de reset. Motor antigo não manda: tratado como definida. */
  resetPasswordDefined?: boolean
  /** Explicit false blocks destructive submission while safe DB maintenance is unavailable. */
  databaseResetReady?: boolean
}

export interface AdminResetResult {
  success: boolean
  message?: string
  tablesCleared?: number
  durationMs?: number
}

/**
 * Admin entity-browser contracts (WS-19 — admin-ux-overhaul §03/§05).
 * The schema response drives which columns are searchable/sortable/filterable;
 * the client never injects a field the server has not validated against DMMF.
 */
export interface AdminModelList {
  count: number
  models: string[]
}

/** Field metadata from `GET /entities/:model/schema` — DMMF-projected. */
export interface AdminEntityField {
  name: string
  /** Prisma scalar/enum type, e.g. `String`, `Int`, `DateTime`, or an enum name. */
  type: string
  /** `scalar` | `enum` (relations are split into `relations`). */
  kind: string
  isRequired: boolean
  isList: boolean
  /** Redacted in list + export; never searchable/sortable/filterable. */
  sensitive: boolean
}

export interface AdminEntityRelation {
  name: string
  type: string
  isList: boolean
  isRequired: boolean
}

export interface AdminEntitySchema {
  modelName: string
  scope: 'global' | 'tenant'
  fields: AdminEntityField[]
  relations: AdminEntityRelation[]
}

export interface AdminEntityPage {
  modelName: string
  page: number
  pageSize: number
  total: number
  pageCount: number
  records: Array<Record<string, unknown>>
}

/**
 * Full-fidelity Work Order shape from `GET /api/v1/work-orders/:id` (engine
 * `work-orders/handlers.ts` `getWorkOrder`) — the rich detail the old admin
 * HTML (`work-orders.html` `renderWoDetail`) used to render. The generic
 * `/admin/entities/:model` list only sends relation `_count`s, so the
 * work-orders DataGrid section fetches this per-row on expand instead
 * (admin-os-detail-panel). Permissive `[k: string]: unknown` — the row keeps
 * whatever other scalar columns the schema-driven grid already shows.
 */
export interface AdminWorkOrderOperation {
  id: string
  sequence: number
  operationNumber?: string | null
  description: string
  plannedDuration?: number | null
  status: string
  technician?: { id: string; name: string } | null
  [k: string]: unknown
}

export interface AdminWorkOrderComponent {
  id: string
  requirementQuantity: number | string
  baseUnit?: string | null
  needsPurchase: boolean
  material?: { id: string; code: string; description: string; unit?: string | null } | null
  [k: string]: unknown
}

export interface AdminWorkOrderNotification {
  id: string
  sequentialNumber?: string | null
  type?: string
  description?: string | null
  [k: string]: unknown
}

export interface AdminWorkOrderDetail {
  id: string
  sequentialNumber?: string | null
  description: string
  longText?: string | null
  equipment?: { id: string; tag: string; description: string } | null
  notification?: AdminWorkOrderNotification | null
  operations: AdminWorkOrderOperation[]
  components: AdminWorkOrderComponent[]
  [k: string]: unknown
}

/* ── Admin custom-view contracts (admin-parity 2026-06-17) ──────────────────
 * Re-built sections from the OLD engine admin HTML, wired to the same
 * `/api/v1/admin/*` (and a few `/api/v1/*` / `/gateway/*`) endpoints. Every
 * call goes through the shared `request()` wrapper (Clerk bearer + tenant
 * headers; no direct fetch). Shapes are intentionally permissive
 * (Record<string, unknown>) for relation-rich rows the views render defensively. */

/** Engine list wrapper used by audit-log, proposals, etc.: `{ data, meta }`. */
export interface AdminListMeta {
  total: number
  page: number
  limit: number
  totalPages: number
}

export interface AdminAuditLogResponse {
  data: Array<Record<string, unknown>>
  meta: AdminListMeta
}

export interface AdminAuditLogQuery {
  page?: number
  limit?: number
  entityType?: string
  action?: string
  dateFrom?: string
  dateTo?: string
}

export interface AdminInventoryTurnoverItem {
  id: string
  code: string
  description: string
  unit: string
  currentStock: number
  reservedStock: number
  available: number
  maxStock: number
  excessQty: number
  capitalImobilizado: number
}

export interface AdminInventoryTurnoverResponse {
  items: AdminInventoryTurnoverItem[]
  page: number
  pageSize: number
  total: number
}

export interface AdminHarnessConfigItem {
  key: string
  value: unknown
  description?: string
  dataType: 'int' | 'string' | 'bool' | 'json' | 'duration'
  scope: 'global' | 'tenant'
  editable: boolean
  hasOverride: boolean
  updatedAt?: string
  updatedByName?: string | null
}

export interface AdminHarnessConfigList {
  items: AdminHarnessConfigItem[]
}

export interface AdminHarnessConfigPutBody {
  value: unknown
  scope: 'global' | 'tenant'
  dataType: 'int' | 'string' | 'bool' | 'json' | 'duration'
  description?: string
  editable?: boolean
}

export interface AdminProposalsResponse {
  data: Array<Record<string, unknown>>
  meta: AdminListMeta
}

export interface AdminGatewaySkill {
  name: string
  description?: string
  triggers?: string[]
  tools?: Array<{ name?: string } | string>
}

export interface AdminGatewayStatus {
  status?: string
  uptime?: number
  skills?: number
  sessions?: Array<Record<string, unknown>>
  [k: string]: unknown
}

export interface AdminI18nConfig {
  supportedLanguages: string[]
  defaultLanguage: string
  fallbackLanguage?: string
  detectionStrategy?: string
  allowUserOverride?: boolean
}

export interface AdminTechnician {
  id: string
  name: string
  role: string
  email?: string | null
  personnelNumber?: string | null
  weeklyAvailableMinutes?: number
  dailyAvailableMinutes?: number
  team?: { name?: string; plant?: { code?: string; name?: string } } | null
  [k: string]: unknown
}

export interface AdminTechniciansResponse {
  data: AdminTechnician[]
  meta?: AdminListMeta
}

export interface UploadResult {
  fileId: string
  fileName: string
  sheets: Array<{ name: string; rowCount: number; columns: string[] }>
  totalRows: number
}

const UPLOAD_TIMEOUT_MS = 180_000

async function uploadFile<T = UploadResult>(path: string, file: File): Promise<T> {
  const { tenantId, userId, getToken } = useAuthStore.getState()
  // S3 US-3.7 — same boundary guard as `getHeaders`.
  if (!tenantId) {
    throw new ApiError(401, 'Missing tenantId — user must authenticate via Clerk')
  }
  const headers: Record<string, string> = {
    'X-Tenant-Id': tenantId,
    'X-User-Id': userId,
  }
  const token = await getToken()
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  const form = new FormData()
  form.append('file', file)

  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), UPLOAD_TIMEOUT_MS)
  let res: Response
  try {
    res = await fetch(`${BASE_URL}${path}`, {
      method: 'POST',
      headers,
      body: form,
      signal: controller.signal,
    })
  } catch {
    if (controller.signal.aborted) {
      throw new ApiError(0, 'Request timed out. The server took too long to respond.')
    }
    throw new ApiError(0, 'Connection lost. Check your network.')
  } finally {
    clearTimeout(timer)
  }

  if (res.status === 401) {
    await handleUnauthorized()
    throw new ApiError(401, 'Session expired')
  }

  if (res.status === 403) {
    throw new ApiError(403, 'Permission denied')
  }

  if (res.status === 429) {
    throw new ApiError(429, 'Too many requests')
  }

  if (!res.ok) {
    const text = await res.text().catch(() => 'Unknown error')
    throw toApiError(res.status, text)
  }

  markSessionHealthy()

  return res.json() as Promise<T>
}

/**
 * Lê o estado durável de um import job (fonte de verdade do watchdog).
 * Usa o mesmo wrapper `request` — herda auth headers, 401/403/429 handling.
 */
export async function getImportJobStatus(jobId: string): Promise<ImportJobStatusResponse> {
  return request<ImportJobStatusResponse>('GET', `/api/v1/import/jobs/${jobId}/status`)
}

/**
 * P0-7 Parte B — lê a página de linhas que falharam num import job.
 * Contrato travado com o engine: `GET /jobs/:jobId/failed-rows?limit&offset`
 * → `{ data: { total, rows: [{sheetName, rowNumber, field, message}] } }`.
 * Paginação server-side porque um import pode ter milhares de linhas com
 * falha — o modal nunca carrega tudo de uma vez (`ImportFailedRowsModal`).
 */
export async function getImportFailedRows(
  jobId: string,
  { limit, offset }: { limit?: number; offset?: number } = {},
): Promise<ImportFailedRowsResponse> {
  const sp = new URLSearchParams()
  if (limit !== undefined) sp.set('limit', String(limit))
  if (offset !== undefined) sp.set('offset', String(offset))
  const qs = sp.toString()
  return request<ImportFailedRowsResponse>(
    'GET',
    `/api/v1/import/jobs/${encodeURIComponent(jobId)}/failed-rows${qs ? `?${qs}` : ''}`,
  )
}

/**
 * STO-1512 — lê a página de linhas que a importação DESCARTOU de propósito
 * (duplicata numa reimportação idempotente), irmã de `getImportFailedRows`.
 * Contrato travado com o engine (STO-1469, PR #2622): `GET
 * /jobs/:jobId/skipped-rows?limit&offset` → `{ data: { total, skipped, rows:
 * [{id, sheetName, rowNumber, reason, message, rawData}] } }`. Paginação
 * server-side pelo mesmo motivo do irmão: um import pode ter milhares de
 * linhas ignoradas.
 */
export async function getImportSkippedRows(
  jobId: string,
  { limit, offset }: { limit?: number; offset?: number } = {},
): Promise<ImportSkippedRowsResponse> {
  const sp = new URLSearchParams()
  if (limit !== undefined) sp.set('limit', String(limit))
  if (offset !== undefined) sp.set('offset', String(offset))
  const qs = sp.toString()
  return request<ImportSkippedRowsResponse>(
    'GET',
    `/api/v1/import/jobs/${encodeURIComponent(jobId)}/skipped-rows${qs ? `?${qs}` : ''}`,
  )
}

/**
 * A planta que o operador cadastrou SEM sair da conversa, no card de
 * dependências da importação.
 *
 * É o cadastro NORMAL de planta (`POST /api/v1/plants`), de propósito: a
 * política de auto-criação do importador não muda: planta continua sendo a
 * única dependência que ele nunca fabrica sozinho (invariante planta-primeiro,
 * #1186). O que o formulário inline entrega é o "alguém olhar" que essa regra
 * exige — país e fuso escritos por quem sabe —, e daí em diante a planta é uma
 * planta como qualquer outra, com registro de auditoria escrito pelo motor.
 */
export async function createPlant(body: {
  code: string
  name: string
  country: string
  timezone: string
}): Promise<{ data: { id: string; code: string; name: string } }> {
  return request('POST', '/api/v1/plants', body)
}

/**
 * Reenvia à esteira SÓ as linhas que o operador corrigiu — nunca o arquivo
 * inteiro, e nunca as ~1.480 linhas que já entraram certas.
 *
 * Contrato travado com o motor: `POST /jobs/:jobId/reimport-rows` com
 * `{ rows: [{ rowId, corrections }] }` → `{ data: { results, summary } }`.
 * SEMPRE lista: 22 falhas em 22 idas e voltas seria construir a lentidão de
 * propósito. A resposta é POR LINHA, com 200 mesmo quando algumas falham —
 * uma correção inválida no meio não pode custar as outras.
 */
export async function reimportImportRows(
  jobId: string,
  rows: ImportRowCorrection[],
): Promise<ImportReimportResponse> {
  return request<ImportReimportResponse>(
    'POST',
    `/api/v1/import/jobs/${encodeURIComponent(jobId)}/reimport-rows`,
    { rows },
  )
}

/** Single operation as returned by the engine `work-orders` REST includes. */
export interface EngineWorkOrderOperation {
  sequence?: number
  description?: string
  status?: string
  /** Planned duration in minutes. */
  plannedDuration?: number | null
  /** ISO datetime string. */
  actualStartDate?: string | null
  /** ISO datetime string. */
  actualEndDate?: string | null
  [k: string]: unknown
}

/**
 * P0-1 (2026-08-14) — raw Work Order shape from the engine's
 * `GET /api/v1/work-orders/by-number/:woNumber` contract, WITH includes
 * (equipment/location/mainTeam/operations). Permissive `[k: string]: unknown`
 * the same way `AdminWorkOrderDetail` is: the mapper below (in
 * `src/features/citations`) only reads the fields it needs to build the
 * artifacts-panel `WoPayload`.
 */
export interface EngineWorkOrder {
  sequentialNumber?: string | null
  description?: string | null
  status?: string | null
  priority?: string | null
  equipment?: { tag?: string | null; description?: string | null } | null
  location?: { name?: string | null } | null
  mainTeam?: { name?: string | null } | null
  plannedStart?: string | null
  plannedEnd?: string | null
  operations?: EngineWorkOrderOperation[]
  [k: string]: unknown
}

/**
 * P0-1 (2026-08-14) — resolves a Work Order by its human-readable sequential
 * number (what the `[wo:OS-2026-0142]` citation chip carries), not by
 * internal id. Tenant-scoped, same auth as every other REST call (through
 * the shared `request` wrapper — no direct `fetch`, no citation resolver
 * mock). 404 (WO not found) propagates as `ApiError(404)`; the caller
 * (`CitationChip`) decides the honest "unavailable" UI, never a JSON dump.
 */
export function getWorkOrderByNumber(woNumber: string): Promise<{ data: EngineWorkOrder }> {
  return request<{ data: EngineWorkOrder }>(
    'GET',
    `/api/v1/work-orders/by-number/${encodeURIComponent(woNumber)}`,
  )
}

/**
 * O CATÁLOGO DE PAPEL DO TÉCNICO — fonte única de verdade (engine
 * `TECHNICIAN_ROLE_CATALOG`, `src/services/team/technician-role-catalog.ts`).
 *
 * 2026-08-25 (#1649/#1467): a lista de papel do "Cadastro de Equipe" tinha um
 * array fixo com os 4 cargos antigos, escrito antes do motor suportar 16 —
 * Lubrificador e Operador não apareciam mesmo já sendo aceitos pelo motor.
 * `TeamRosterTable` (via `useTechnicianRoles`) passa a buscar esta lista em
 * vez de manter cópia própria.
 */
export interface TechnicianRoleCatalogEntry {
  /** O valor gravado no banco (`TechnicianRole` do Prisma). */
  value: string
  /** Rótulo do motor — SEMPRE pt-BR (o motor não traduz). */
  label: string
  sortOrder: number
  teamSpecialtySlug: string
}

/**
 * `GET /api/v1/technicians/roles` — rota estática (não `/:id`), atrás de
 * `tenantMiddleware`, sem dado de cliente. Qualquer sessão autenticada do
 * tenant alcança (não é rota `admin`).
 */
export function getTechnicianRoles(): Promise<{ data: TechnicianRoleCatalogEntry[] }> {
  return request<{ data: TechnicianRoleCatalogEntry[] }>('GET', '/api/v1/technicians/roles')
}

/**
 * Filtros comuns de `GET /schedule` e `GET /schedule/export` (spec §3.4) — os
 * mesmos filtros para não deixarem a lista e o export divergirem.
 */
export interface ScheduleFilterParams {
  scenarioId?: string
  /** Abre a versão salva mais recente da linhagem explícita ou do cenário padrão. */
  latest?: boolean
  equipmentIds?: string[]
  equipmentCriticality?: ScheduleCriticality[]
  taskCriticality?: ScheduleCriticality[]
  plantId?: string
}

/**
 * Recortes da GRADE que só o export recebe (QA 22.1, ST-Operations-os#84): o
 * arquivo sai com o que o gestor vê. O motor aplica cada um sobre o envelope já
 * escopado por tenant (`schedule-view-filters.ts`, mesma regra da tela).
 */
export interface ScheduleExportParams extends ScheduleFilterParams {
  versionId?: string
  equipmentTaskCriticality?: ScheduleCriticality[]
  onlyInferred?: boolean
  equipmentSearch?: string
  periodFrom?: string
  periodTo?: string
}

function scheduleViewQuery(sp: URLSearchParams, params: ScheduleExportParams): void {
  if (params.versionId) sp.set('versionId', params.versionId)
  if (params.equipmentTaskCriticality?.length) {
    sp.set('equipmentTaskCriticality', params.equipmentTaskCriticality.join(','))
  }
  if (params.onlyInferred) sp.set('onlyInferred', 'true')
  if (params.equipmentSearch) sp.set('equipmentSearch', params.equipmentSearch)
  if (params.periodFrom) sp.set('periodFrom', params.periodFrom)
  if (params.periodTo) sp.set('periodTo', params.periodTo)
}

function scheduleFilterQuery(params: ScheduleFilterParams): URLSearchParams {
  const sp = new URLSearchParams()
  if (params.scenarioId) sp.set('scenarioId', params.scenarioId)
  if (params.latest) sp.set('latest', 'true')
  if (params.equipmentIds?.length) sp.set('equipmentIds', params.equipmentIds.join(','))
  if (params.equipmentCriticality?.length) {
    sp.set('equipmentCriticality', params.equipmentCriticality.join(','))
  }
  if (params.taskCriticality?.length) sp.set('taskCriticality', params.taskCriticality.join(','))
  if (params.plantId) sp.set('plantId', params.plantId)
  return sp
}

/**
 * `GET /api/v1/planning/schedule` (spec §3.4) — a grade completa (52/53
 * semanas × equipamento › plano › tarefas), `compact:false`, para o
 * `ScheduleBoard`. Teto do motor: 300 planos; acima, `400 { code:
 * 'schedule_too_large' }` propaga como `ApiError`.
 */
export function getSchedule(params: ScheduleFilterParams = {}): Promise<LtpScheduleEnvelope> {
  const qs = scheduleFilterQuery(params).toString()
  return request<LtpScheduleEnvelope>('GET', `/api/v1/planning/schedule${qs ? `?${qs}` : ''}`)
}

/** Typed schedule adapter; action authority and idempotency live in the adjacent domain helper. */
export const postScheduleAction = createScheduleActionSender(request)

/** Linha de `GET /api/v1/planning/scenarios` (`ScenarioListItem` do motor): só o que o filtro de ano lê. */
export interface PlanningScenarioItem {
  id: string
  name: string
  anchorDate: string
  baselineYear: number | null
  createdAt: string
}

/**
 * Todos os cenários do inquilino (o motor pagina em até 100). O filtro de ano do cronograma só
 * oferece anos em que existe cenário simulado de verdade: o motor não gera cenário por GET.
 */
export async function listPlanningScenarios(): Promise<PlanningScenarioItem[]> {
  const items: PlanningScenarioItem[] = []
  for (let page = 1; page <= 10; page++) {
    const result = await request<{ items: PlanningScenarioItem[]; hasMore: boolean }>(
      'GET',
      `/api/v1/planning/scenarios?page=${page}&pageSize=100`,
    )
    items.push(...result.items)
    if (!result.hasMore || result.items.length === 0) break
  }
  return items
}

/**
 * `GET /api/v1/planning/schedule/export` (spec §3.4) — mesmos filtros de
 * `getSchedule` + `format`. Sibling de `exportEntities` (api.ts:1061): passa
 * por `downloadBlob`, não por uma URL crua — o motor exige o mesmo Bearer/
 * tenant header de toda rota autenticada, que uma âncora `<a href>` não
 * consegue anexar.
 */
export function downloadScheduleExport(
  params: ScheduleExportParams & { format: 'xlsx' | 'pdf' },
): Promise<void> {
  const sp = scheduleFilterQuery(params)
  scheduleViewQuery(sp, params)
  sp.set('format', params.format)
  return downloadBlob(
    `/api/v1/planning/schedule/export?${sp.toString()}`,
    `cronograma-52-semanas.${params.format}`,
  )
}

export interface ScheduleTechnicianOption {
  id: string
  name: string
}

export interface ScheduleTeamOption {
  id: string
  name: string
}

/**
 * Técnicos do tenant para o `InlineEditPopover` (Task 40, STO-529/F6, spec
 * §2.5) — reaproveita `api.admin.technicians` (`GET /api/v1/technicians`,
 * já existente, `api.ts:1136`), NENHUMA rota nova no motor. Página única de
 * 50 cobre o tenant típico desta fatia; busca incremental fica para quem
 * precisar de paginação de verdade.
 */
export function getScheduleTechnicians(): Promise<ScheduleTechnicianOption[]> {
  return api.admin.technicians({}).then((r) => r.data.map((t) => ({ id: t.id, name: t.name })))
}

/**
 * Equipes do tenant — `GET /api/v1/teams` (motor, `src/api/v1/teams/routes.ts`,
 * `listTeams`), medido em `origin/develop` do motor: rota já existe, nunca
 * consumida por este repositório até agora. Sem isto, `patch.teamId` do
 * popover não teria de onde vir uma equipe REAL para oferecer — a spec
 * assumiu "via api.ts já existentes"; o que já existe é a rota do motor, o
 * wrapper aqui é o que faltava do lado da tela.
 */
export function getScheduleTeams(): Promise<ScheduleTeamOption[]> {
  return request<{ data: ScheduleTeamOption[] }>('GET', '/api/v1/teams').then((r) =>
    r.data.map((t) => ({ id: t.id, name: t.name })),
  )
}

/**
 * P0-3 — citation types the assistant emits inline in chat (`[doc:...]`,
 * `[wo:...]`, `[norma:...]`, `[equip:...]`). Structurally mirrors
 * `src/features/citations/parseCitations.ts` `CitationType`, kept as an
 * independent declaration here so `lib/` never imports from `features/`.
 */
export type CitationSourceType = 'doc' | 'wo' | 'norma' | 'equip'

/** Engine `GET /api/v1/citations/:type/:id` success shape (locked contract). */
export interface CitationSource {
  type: CitationSourceType
  id: string
  title: string
  snippet: string
  url?: string
  metadata?: Record<string, unknown>
}

/**
 * P0-3 (2026-08-14) — the unified citation resolver. Engine PR
 * `feat/citations-resolver-endpoint`; wired here behind
 * `VITE_FEATURE_CITATION_ENDPOINT` (default off, see
 * `src/features/citations/citation-resolver-flags.ts`) until that PR
 * deploys — the resolver falls back to the existing `/api/v1/equipment` and
 * `/api/v1/maint-plans/:id` endpoints until then.
 *
 * 404 (not found in tenant) and 501 `connector_not_configured` (type has no
 * connector yet — `wo`/`norma` today) both propagate as `ApiError`; the
 * caller (`source-resolver.ts`) turns them into the honest `unavailable`
 * state, never a fabricated title/snippet. 10s timeout — this is an
 * on-hover call, not a page load.
 */
function resolveCitation(type: CitationSourceType, id: string): Promise<{ data: CitationSource }> {
  return request<{ data: CitationSource }>(
    'GET',
    `/api/v1/citations/${encodeURIComponent(type)}/${encodeURIComponent(id)}`,
    undefined,
    10_000,
  )
}

const PUBLIC_TIMEOUT_MS = 30_000

/**
 * P0-6 (2026-08-14) — GET wrapper for the small set of engine endpoints that
 * are public BY DESIGN (today: the invitation preview, `GET /api/v1/
 * invitations/:token`) — the caller has no Clerk session yet, so there is no
 * tenant/user/JWT to attach.
 *
 * Deliberately NOT `request()`: that function (a) calls `getHeaders()`, which
 * THROWS when `tenantId` is empty — exactly the pre-auth state this path
 * exists for — and (b) treats any 401 as "session expired" and fires
 * `logout()`, which would be wrong here: a 401 from a public, unauthenticated
 * endpoint is just a normal error response (e.g. a future auth requirement),
 * never a sign of a stale LOCAL session to tear down. `getPublic` sends no
 * `Authorization` / `X-Tenant-Id` / `X-User-Id` headers and never touches the
 * auth store — it shares only `BASE_URL`, the timeout/abort shape and
 * `ApiError`/`toApiError` with the authenticated path, so a public endpoint's
 * error body is still structured the same way for callers.
 *
 * NOT for accept — `POST /invitations/:token/accept` requires a verified
 * Clerk session (engine `requireAuth`, P0-6) and goes through the normal
 * authenticated `api.post`.
 */
async function requestPublic<T>(path: string, timeoutMs: number = PUBLIC_TIMEOUT_MS): Promise<T> {
  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), timeoutMs)
  let res: Response
  try {
    res = await fetch(`${BASE_URL}${path}`, {
      method: 'GET',
      headers: { Accept: 'application/json' },
      signal: controller.signal,
    })
  } catch {
    if (controller.signal.aborted) {
      throw new ApiError(0, 'Request timed out. The server took too long to respond.')
    }
    throw new ApiError(0, 'Connection lost. Check your network.')
  } finally {
    clearTimeout(timer)
  }

  if (!res.ok) {
    const text = await res.text().catch(() => 'Unknown error')
    throw toApiError(res.status, text)
  }

  return res.json() as Promise<T>
}

const DOWNLOAD_TIMEOUT_MS = 120_000

/**
 * Authenticated binary download (the JSON-only `request()` cannot stream a file).
 *
 * Sibling of `uploadFile`: attaches the Clerk bearer + tenant headers via the
 * same boundary guard, fetches, reads `res.blob()`, and triggers an anchor
 * download — so the SPA never builds the .xlsx itself (export is server-side,
 * full filtered set). Surfaces 401/403/429 identically to `request()` so the
 * admin gate (403) propagates as `ApiError`.
 */
async function fetchBlob(path: string, redirect?: RequestRedirect): Promise<Blob> {
  const { tenantId, userId, getToken } = useAuthStore.getState()
  if (!tenantId) {
    throw new ApiError(401, 'Missing tenantId — user must authenticate via Clerk')
  }
  const headers: Record<string, string> = {
    'X-Tenant-Id': tenantId,
    'X-User-Id': userId,
  }
  const token = await getToken()
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  const controller = new AbortController()
  const timer = setTimeout(() => controller.abort(), DOWNLOAD_TIMEOUT_MS)
  let res: Response
  try {
    res = await fetch(`${BASE_URL}${path}`, {
      method: 'GET',
      ...(redirect ? { redirect } : {}),
      headers,
      signal: controller.signal,
    })
  } catch {
    if (controller.signal.aborted) {
      throw new ApiError(0, 'Request timed out. The server took too long to respond.')
    }
    throw new ApiError(0, 'Connection lost. Check your network.')
  } finally {
    clearTimeout(timer)
  }

  if (res.status === 401) {
    await handleUnauthorized()
    throw new ApiError(401, 'Session expired')
  }
  if (res.status === 403) {
    throw new ApiError(403, 'Permission denied')
  }
  if (res.status === 429) {
    throw new ApiError(429, 'Too many requests')
  }
  if (!res.ok) {
    const text = await res.text().catch(() => 'Unknown error')
    throw toApiError(res.status, text)
  }
  markSessionHealthy()

  return res.blob()
}

async function downloadBlob(
  path: string,
  filename: string,
  redirect?: RequestRedirect,
): Promise<void> {
  const blob = await fetchBlob(path, redirect)
  const url = URL.createObjectURL(blob)
  try {
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    document.body.appendChild(a)
    a.click()
    a.remove()
  } finally {
    URL.revokeObjectURL(url)
  }
}

/** Use the configured authenticated API, never the producer's possibly different host alias. */
export async function downloadImportTemplate(
  fileId: string,
  entity: string,
  fileName: string,
): Promise<void> {
  if (
    !/^[a-f0-9]{8}(?:-[a-f0-9]{4}){3}-[a-f0-9]{12}$/i.test(fileId) ||
    !/^[a-z0-9_-]+$/i.test(entity)
  ) {
    throw new ApiError(400, 'Invalid template download identity')
  }
  return downloadBlob(
    `/api/v1/import/templates/${fileId}/download?entity=${encodeURIComponent(entity.toLowerCase())}`,
    fileName,
    'error',
  )
}

export const api = {
  presentationPost: <T>(body: unknown, owner: { tenantId: string; userId: string }) =>
    requestPresentation<T>(body, owner),
  get: <T>(path: string, owner?: { tenantId: string; userId: string }) =>
    request<T>('GET', path, undefined, DEFAULT_TIMEOUT_MS, undefined, owner),
  post: <T>(path: string, body: unknown, timeoutMs?: number) =>
    request<T>('POST', path, body, timeoutMs),
  resolveCitation,
  patch: <T>(path: string, body: unknown) => request<T>('PATCH', path, body),
  del: <T>(path: string) => request<T>('DELETE', path),
  upload: (file: File) => uploadFile('/api/v1/import/upload', file),
  /** Foto/arquivo durável (`POST /api/v1/files`); resposta `{ data: { id } }`. */
  uploadDurableFile: (file: File) => uploadFile<{ data?: { id?: string } }>('/api/v1/files', file),
  /** Bytes autenticados (ex.: foto) — o chamador cria/revoga o object URL. */
  getBlob: (path: string) => fetchBlob(path),
  /** See {@link requestPublic} — pre-auth GET only, no headers, no logout()-on-401. */
  getPublic: <T>(path: string) => requestPublic<T>(path),

  /**
   * Deterministic import — multi-sheet analysis of an already-uploaded
   * workbook. Bypasses the LLM entirely (engine `analyzeWorkbook` +
   * `resolveDependencies`). Returns the engine-shaped proposal.
   * 90 s timeout: analysis is CPU-bound on the engine, can take ~30-60 s for
   * large workbooks.
   */
  analyzeImport: (fileId: string) =>
    request<EngineMultiSheetProposal>('POST', '/api/v1/import/analyze', { fileId }, 90_000),

  /**
   * Deterministic import — dual-tolerant execute (ADR-307-A).
   *
   * When IMPORT_EXECUTE_ASYNC is ON (engine flag): returns HTTP 202
   * `ExecuteImportEnqueuedResult { jobId, proposalId, jobState }` and the
   * client seeds the job store + emits a live job card.
   *
   * When IMPORT_EXECUTE_ASYNC is OFF (engine default): returns HTTP 200
   * `EngineMultiSheetResult` synchronously and the client sets
   * executeResult + step 'done' as before.
   *
   * Use `isEnqueuedResult(res)` to branch between the two shapes.
   * Body MUST be the engine shape `{ fileId, sheetPlans, importOrder, ... }`
   * (NOT the legacy `{ proposalId, approvedSheets }`, which the engine rejects
   * with 400). Optional `proposalId` enables idempotency on retry.
   * 120 s timeout: generous ceiling so the client surfaces a recoverable error
   * instead of an infinite spinner if the engine OOM-dies mid-flight.
   */
  executeImport: (body: ExecuteImportBody) =>
    request<ExecuteImportEnqueuedResult | EngineMultiSheetResult>(
      'POST',
      '/api/v1/import/execute',
      body,
      120_000,
    ),

  /**
   * RAIZ 9 (2026-07-25) — decide uma aba de confiança parcial (`needs_confirm`).
   * `accept` importa a aba como a entidade sugerida, `ignore` descarta a aba.
   * Não existe terceira opção. Antes essa decisão só existia como tool MCP, então
   * quem subiu a planilha pela tela ficava sem saída depois da recusa do
   * `/execute`: era o beco sem saída do defeito G7.
   */
  resolveSheetSuggestion: (suggestionId: string, decision: 'accept' | 'ignore') =>
    request<{ suggestionId: string; status: 'accepted' | 'ignored'; jobId?: string }>(
      'POST',
      `/api/v1/import/suggestions/${suggestionId}/resolve`,
      { decision },
    ),

  /**
   * Admin REST surface. Every endpoint is server-gated by `requireAdmin`
   * (engine), so a non-admin Clerk session receives 403 here (ApiError(403)).
   * Goes through the shared `request` wrapper — attaches the Clerk bearer +
   * tenant headers; no direct `fetch` (frontend rule).
   */
  admin: {
    dashboard: () => request<AdminDashboardResponse>('GET', '/api/v1/admin/dashboard'),

    // --- Especialidades (CRUD de Especialidades) — ADMIN-ONLY ---
    /** Lista especialidades do tenant. `includeInactive` traz também as desativadas. */
    listSpecialties: (params?: { includeInactive?: boolean; language?: string }) => {
      const sp = new URLSearchParams()
      if (params?.includeInactive) sp.set('includeInactive', 'true')
      const qs = sp.toString()
      return request<{ data: AdminSpecialty[] }>(
        'GET',
        `/api/v1/specialties${qs ? `?${qs}` : ''}`,
        undefined,
        DEFAULT_TIMEOUT_MS,
        params?.language ? { 'Accept-Language': params.language } : undefined,
      )
    },
    /** Cria especialidade. `slug` é opcional (derivado do `name` via slugify no engine). */
    createSpecialty: (body: { name: string; slug?: string }) =>
      request<{ data: AdminSpecialty }>('POST', '/api/v1/specialties', body),
    /** Edita nome e/ou estado ativo. `slug`/`isDefault` não são editáveis via API. */
    updateSpecialty: (id: string, body: { name?: string; active?: boolean }) =>
      request<{ data: AdminSpecialty }>(
        'PATCH',
        `/api/v1/specialties/${encodeURIComponent(id)}`,
        body,
      ),
    /** Soft-delete (204). 409 se for seed do sistema ou estiver em uso por equipes. */
    deleteSpecialty: (id: string) =>
      request<void>('DELETE', `/api/v1/specialties/${encodeURIComponent(id)}`),

    // --- RESET TOTAL DA BASE (admin-reset-total) ---
    /** Whether the in-admin base-reset control should be shown on this base. Engine GET /reset-demo. */
    resetTotalAvailability: () =>
      request<AdminResetAvailability>('GET', '/api/v1/admin/reset-demo'),
    // Owner-fenced execution and read-only operation recovery; passwords are never retained.
    ...createResetExecutionApi(request),
    /** First time only: defines the tenant reset password (`{ newPassword }`). */
    defineResetPassword: async (newPassword: string) =>
      request<{ resetPasswordDefined: true }>(
        'POST',
        RESET_PASSWORD_PATH,
        resetPasswordInputSchema.parse({ newPassword }),
      ),
    /** Changes it: the current one goes through the same nonce + 3-failure lock as the reset. */
    changeResetPassword: async (currentPassword: string, newPassword: string) => {
      const attempt = resetReauthAttemptSchema.parse(
        await request<unknown>('POST', RESET_REAUTH_ATTEMPT_PATH, {}),
      )
      const body = resetPasswordInputSchema.parse({
        newPassword,
        currentPassword,
        attemptId: attempt.attemptId,
      })
      return request<{ resetPasswordDefined: true }>('POST', RESET_PASSWORD_PATH, body)
    },

    // --- Entity browser (WS-19 / DataGrid) ---
    /** Browsable model names for the entity-browser sidebar. */
    listModels: () => request<AdminModelList>('GET', '/api/v1/admin/entities/list-models'),
    /** Field metadata for one model — drives typed columns/filters/sort. */
    schema: (model: string) =>
      request<AdminEntitySchema>('GET', `/api/v1/admin/entities/${model}/schema`),
    /**
     * Paginated, sanitized, filtered records. `qs` is the prebuilt querystring
     * from `buildEntityQuerystring` (q + filters + sort + page) so list and
     * export can never diverge.
     */
    listEntities: (model: string, qs: string) =>
      request<AdminEntityPage>('GET', `/api/v1/admin/entities/${model}?${qs}`),
    /**
     * Server-side .xlsx export of the FULL filtered set (NOT the visible page).
     * Reuses the SAME `qs` as the list; the engine reuses the same
     * `buildEntityQuery`. Blob path (request() is JSON-only); 403 propagates
     * as ApiError (admin gate). 413 if the filtered set exceeds the engine ceiling.
     */
    exportEntities: (model: string, qs: string) =>
      downloadBlob(`/api/v1/admin/entities/${model}/export?${qs}`, `${model}.xlsx`),
    /**
     * Full Work Order detail (operations w/ technician, materials, linked
     * notification) — NOT under `/admin/entities`; the plain tenant-scoped
     * `/api/v1/work-orders/:id` (requires only auth, same as `/proposals` and
     * `/specialties` above). Backs the work-orders section's row-expand panel.
     */
    getWorkOrder: (id: string) =>
      request<{ data: AdminWorkOrderDetail }>(
        'GET',
        `/api/v1/work-orders/${encodeURIComponent(id)}`,
      ),

    // --- Custom-view sections (admin-parity 2026-06-17) ---
    /** Audit-log feed — `{ data, meta }`; supports entityType/action/date filters. */
    auditLog: (query: AdminAuditLogQuery = {}) => {
      const sp = new URLSearchParams()
      if (query.page) sp.set('page', String(query.page))
      sp.set('limit', String(query.limit ?? 50))
      if (query.entityType) sp.set('entityType', query.entityType)
      if (query.action) sp.set('action', query.action)
      if (query.dateFrom) sp.set('dateFrom', query.dateFrom)
      if (query.dateTo) sp.set('dateTo', query.dateTo)
      return request<AdminAuditLogResponse>('GET', `/api/v1/admin/audit-log?${sp.toString()}`)
    },

    /** Inventory turnover — EXCESSO materials by capital imobilizado (plant-scoped). */
    inventoryTurnover: (plantId: string, page = 1, search = '') => {
      const sp = new URLSearchParams()
      sp.set('plantId', plantId)
      sp.set('page', String(page))
      sp.set('pageSize', '20')
      if (search.trim()) sp.set('search', search.trim())
      return request<AdminInventoryTurnoverResponse>(
        'GET',
        `/api/v1/admin/inventory-turnover?${sp.toString()}`,
      )
    },

    /** Harness config (whitelist) — list / upsert / reset-to-default. */
    harnessConfig: () => request<AdminHarnessConfigList>('GET', '/api/v1/admin/harness-config/'),
    setHarnessConfig: (key: string, body: AdminHarnessConfigPutBody) =>
      request<unknown>('PUT', `/api/v1/admin/harness-config/${encodeURIComponent(key)}`, body),
    resetHarnessConfig: (key: string) =>
      request<unknown>('DELETE', `/api/v1/admin/harness-config/${encodeURIComponent(key)}`),

    /** AI Proposals — `{ data, meta }`; PUT approve/reject (engine 1:1 endpoints). */
    proposals: (status = 'all', page = 1) => {
      const sp = new URLSearchParams()
      sp.set('page', String(page))
      sp.set('limit', '20')
      if (status && status !== 'all') sp.set('status', status)
      return request<AdminProposalsResponse>('GET', `/api/v1/proposals?${sp.toString()}`)
    },
    approveProposal: (id: string, reviewerId: string) =>
      request<unknown>('PUT', `/api/v1/proposals/${id}/approve`, { reviewerId }),
    rejectProposal: (id: string, reviewerId: string) =>
      request<unknown>('PUT', `/api/v1/proposals/${id}/reject`, { reviewerId }),

    /** Skill-gateway loaded skills (read-only). */
    skills: () => request<AdminGatewaySkill[]>('GET', '/gateway/skills'),
    /** Skill-gateway status + optional live sessions. */
    gatewayStatus: () => request<AdminGatewayStatus>('GET', '/gateway/status'),

    /** i18n config — current language settings + per-key update. */
    i18nConfig: () => request<AdminI18nConfig>('GET', '/api/v1/i18n/config'),
    setI18nConfig: (key: string, value: unknown) =>
      request<unknown>('PUT', `/api/v1/i18n/config/${encodeURIComponent(key)}`, { value }),

    /** Recursos / technicians — paginated list (search + plant + role filters). */
    technicians: (
      params: { page?: number; search?: string; plantId?: string; role?: string } = {},
    ) => {
      const sp = new URLSearchParams()
      sp.set('page', String(params.page ?? 1))
      sp.set('limit', '50')
      if (params.search?.trim()) sp.set('search', params.search.trim())
      if (params.plantId) sp.set('plantId', params.plantId)
      if (params.role) sp.set('role', params.role)
      return request<AdminTechniciansResponse>('GET', `/api/v1/technicians?${sp.toString()}`)
    },
  },
}

export { ApiError }

/** Existing tenant-scoped catalog routes, paginated until complete. */
export interface DetailingTeamOption {
  id: string
  name: string
  plantId: string
  plant: { name: string }
  specialtyId: string
  specialty: { name: string }
}
export interface DetailingMaterialOption {
  id: string
  code: string
  description: string
  plantId: string
}
async function detailingCatalog<T>(path: string): Promise<T[]> {
  const rows: T[] = []
  for (let page = 1; ; page++) {
    const result = await request<{ data: T[]; meta: { total: number } }>(
      'GET',
      `${path}${path.includes('?') ? '&' : '?'}page=${page}&limit=100`,
    )
    rows.push(...result.data)
    if (result.data.length === 0 || rows.length >= result.meta.total) return rows
  }
}
export const getDetailingTeams = () => detailingCatalog<DetailingTeamOption>('/api/v1/teams')
export const getDetailingMaterials = () =>
  detailingCatalog<DetailingMaterialOption>('/api/v1/materials?sortBy=code&sortOrder=asc')
export const getDetailingTechnicians = (teamId: string) =>
  request<{
    data: {
      primaryMembers: Array<{ id: string; name: string }>
      additionalMembers: Array<{ id: string; name: string }>
    }
  }>('GET', `/api/v1/teams/${encodeURIComponent(teamId)}/technicians/all`).then(({ data }) => [
    ...data.primaryMembers,
    ...data.additionalMembers,
  ])
